import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useLocation, useSearchParams } from 'react-router';

import { AppRoutes, THROTTLE_LIMIT } from '@/constants';
import { formatDate } from '@/helpers';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';

import { CloseIcon } from '@/assets/icons';
import {
  Button,
  DatePicker,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  SortDirection,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import { Classification, ProcessingStatus } from '@/features/regulatory-intake/enums';
import {
  getClassificationLabel,
  getProcessingStatusIcon,
  getProcessingStatusLabel,
  getProcessingStatusVariant,
} from '@/features/regulatory-intake/utils';

import { PROCESSING_STATUS_OPTIONS } from '@/features/regulatory-intake/constants';
import { NotificationField } from '@/features/regulatory-intake/intake-list/enums';
import { useNotificationSearching } from '@/features/regulatory-intake/intake-list/hooks';
import {
  INotification,
  INotificationSearchingPayload,
} from '@/features/regulatory-intake/intake-list/interfaces';

import { ProcessingStatusOverview } from './ProcessingStatusOverview';

export const Notifications = () => {
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [statuses, setStatuses] = useState<string[]>(
    searchParams.get('statuses')?.split(',').filter(Boolean) || [],
  );
  const [receivedFrom, setReceivedFrom] = useState<Date | undefined>(
    searchParams.get('from') ? new Date(searchParams.get('from') as string) : undefined,
  );
  const [receivedTo, setReceivedTo] = useState<Date | undefined>(
    searchParams.get('to') ? new Date(searchParams.get('to') as string) : undefined,
  );

  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || NotificationField.Id);
  const [isDescending, setDescending] = useState<boolean>(searchParams.get('desc') !== 'false');

  const previousFilterKeyRef = useRef<string | null>(null);
  const receivedFromPickerRef = useRef<any>(null);
  const receivedToPickerRef = useRef<any>(null);

  const isFiltered = useMemo(
    () =>
      searchQuery.length > 0 ||
      statuses.length > 0 ||
      receivedFrom !== undefined ||
      receivedTo !== undefined,
    [statuses, receivedFrom, receivedTo, searchQuery],
  );

  const onClearFilters = () => {
    navigate(
      {
        pathname: routerLocation.pathname,
        hash: routerLocation.hash,
      },
      { replace: true },
    );
    setSearchQuery('');
    setStatuses([]);
    setReceivedFrom(undefined);
    setReceivedTo(undefined);
    receivedFromPickerRef.current?.onClear();
    receivedToPickerRef.current?.onClear();
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // // Throttle filters and sorting
  const throttledStatuses = useThrottle(statuses, THROTTLE_LIMIT, true);
  const throttledReceivedFrom = useThrottle(receivedFrom, THROTTLE_LIMIT, true);
  const throttledReceivedTo = useThrottle(receivedTo, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Handle column sorting
  const onSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setDescending(true);
    } else {
      setSortBy(columnKey);
      setDescending(direction === 'desc');
    }
  };

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: INotificationSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    // Divide by 2 because of how enums are compiled in TS
    const statusCount = Object.keys(ProcessingStatus).length / 2;
    if (
      throttledStatuses &&
      throttledStatuses.length > 0 &&
      throttledStatuses.length < statusCount
    ) {
      payload.statuses = throttledStatuses.map((status) => Number(status) as ProcessingStatus);
    }

    if (throttledReceivedFrom) {
      payload.startTime = throttledReceivedFrom.toISOString();
    }

    if (throttledReceivedTo) {
      const endOfDay = new Date(throttledReceivedTo);
      endOfDay.setHours(23, 59, 59, 999);
      payload.endTime = endOfDay.toISOString();
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledStatuses,
    throttledReceivedFrom,
    throttledReceivedTo,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { data, isLoading, isFetching } = useNotificationSearching(apiPayload);

  const notifications = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata?.totalPages || 0, [data?.metadata?.totalPages]);
  const totalItems = useMemo(() => data?.metadata?.totalItems || 0, [data?.metadata?.totalItems]);
  const isEmpty = useMemo(() => notifications.length === 0, [notifications]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (statuses.length) params.set('statuses', statuses.join(','));
    if (receivedFrom) params.set('from', receivedFrom.toISOString());
    if (receivedTo) params.set('to', receivedTo.toISOString());
    if (sortBy !== NotificationField.Id) params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    navigate(
      {
        pathname: routerLocation.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash: routerLocation.hash,
      },
      { replace: true },
    );
  }, [
    currentPage,
    searchQuery,
    statuses,
    receivedFrom,
    receivedTo,
    sortBy,
    isDescending,
    routerLocation.pathname,
    routerLocation.hash,
  ]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledStatuses,
      throttledReceivedFrom,
      throttledReceivedTo,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      // First run — store baseline, don't reset
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [
    debouncedSearchQuery,
    throttledStatuses,
    throttledReceivedFrom,
    throttledReceivedTo,
    throttledSortBy,
    throttledIsDescending,
  ]);

  return (
    <div>
      <div className="flex justify-between w-full">
        <div className="flex flex-row flex-wrap justify-start items-center gap-2">
          <Search
            placeholder="Search by email subject"
            className="w-[340px] sm:w-[340px] gap-2"
            style={{ paddingRight: '16px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={PROCESSING_STATUS_OPTIONS}
            defaultValue={statuses}
            placeholder="Processing status"
            onValueChange={(values) => {
              setStatuses(values);
            }}
            maxCount={1}
            className="!w-fit !min-w-[280px]"
          />
          <DatePicker
            required
            ref={receivedFromPickerRef}
            align="start"
            className="gap-2"
            buttonClassName="!w-fit !min-w-[260px]"
            placeholder="Received from"
            initialDate={receivedFrom}
            // "From" must be <= "To": disable every date after the current `receivedTo`.
            calendarDisabled={(day) => (receivedTo ? day > receivedTo : false)}
            onUpdate={({ date }) => setReceivedFrom(date)}
          />
          <DatePicker
            required
            ref={receivedToPickerRef}
            align="start"
            className="gap-2"
            buttonClassName="!w-fit !min-w-[260px]"
            placeholder="Received to"
            initialDate={receivedTo}
            // "To" must be >= "From": disable every date before the current `receivedFrom`.
            calendarDisabled={(day) => (receivedFrom ? day < receivedFrom : false)}
            onUpdate={({ date }) => setReceivedTo(date)}
          />
          {isFiltered && (
            <Button
              buttonColor="primary"
              buttonStyle="textlink"
              prefixIcon={<CloseIcon className="size-5" />}
              onClick={onClearFilters}
              size="default"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult label="No intake jobs." />
        ) : (
          <>
            <div className="w-full pb-3 mb-3 overflow-auto custom-scrollbar">
              <Table className="mt-4 min-w-full !w-fit !overflow-hidden" size="sm" template="zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === NotificationField.Timestamp
                          ? isDescending
                            ? 'desc'
                            : 'asc'
                          : null
                      }
                      onSort={(direction) => onSort(NotificationField.Timestamp, direction)}
                    >
                      Received time
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === NotificationField.Subject
                          ? isDescending
                            ? 'desc'
                            : 'asc'
                          : null
                      }
                      onSort={(direction) => onSort(NotificationField.Subject, direction)}
                    >
                      Subject
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === NotificationField.Classification
                          ? isDescending
                            ? 'desc'
                            : 'asc'
                          : null
                      }
                      onSort={(direction) => onSort(NotificationField.Classification, direction)}
                    >
                      Classification
                    </TableHead>
                    <TableHead>Links Extracted</TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === NotificationField.ProcessingStatus
                          ? isDescending
                            ? 'desc'
                            : 'asc'
                          : null
                      }
                      onSort={(direction) => onSort(NotificationField.ProcessingStatus, direction)}
                    >
                      Processing Status
                    </TableHead>
                    <TableHead
                      sortable
                      sortDirection={
                        sortBy === NotificationField.JobId ? (isDescending ? 'desc' : 'asc') : null
                      }
                      onSort={(direction) => onSort(NotificationField.JobId, direction)}
                    >
                      Job ID
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((notification: INotification) => (
                    <TableRow key={notification.id} rowId={notification.id}>
                      <TableCell className="min-w-[156px] w-[156px] whitespace-break-spaces text-neutral-9">
                        {notification.timestamp ? (
                          formatDate({
                            date: new Date(notification.timestamp),
                            hasTime: true,
                            hasTimezone: true,
                          })
                        ) : (
                          <span className="tracking-[1px]">--</span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[268px] max-w-[20%]">
                        <span
                          className="line-clamp-2 whitespace-normal break-words text-neutral-9"
                          title={notification.subject}
                        >
                          {notification.subject ? notification.subject : '(no subject)'}
                        </span>
                      </TableCell>
                      <TableCell className="min-w-[200px] max-w-[14%] text-neutral-9">
                        {getClassificationLabel(
                          notification.classification,
                          notification.notRegulatoryReason,
                        )}
                      </TableCell>
                      <TableCell className="min-w-[470px] max-w-[33%]">
                        <ProcessingStatusOverview
                          links={notification.links}
                          isNA={notification.classification === Classification.NotRegulatory}
                        />
                      </TableCell>
                      <TableCell
                        cellType="status"
                        className="min-w-[178px] max-w-[13%]"
                        statusVariant={getProcessingStatusVariant(
                          notification.processingStatus || ProcessingStatus.NotAvailable,
                        )}
                        statusIcon={(() => {
                          const Icon = getProcessingStatusIcon(
                            notification.processingStatus || ProcessingStatus.NotAvailable,
                          );
                          return <Icon className="size-3" />;
                        })()}
                        tooltip={notification.errorLog}
                      >
                        {getProcessingStatusLabel(
                          notification.processingStatus || ProcessingStatus.NotAvailable,
                        )}
                      </TableCell>
                      <TableCell className="font-semibold underline text-primary-base min-w-[120px] max-w-[10%]">
                        <Link
                          to={generatePath(AppRoutes.REGULATORY_INTAKE_DETAILS, {
                            id: notification.jobId,
                          })}
                          className="block truncate"
                          title={notification.jobId}
                        >
                          {notification.jobId}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={(page) => setCurrentPage(page)}
              />
            )}
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
    </div>
  );
};
